// frontend/components/providers/ToastProvider.tsx
"use client";

import {
  createContext,
  useCallback,
  useContext,
  useState,
  type ReactNode,
} from "react";
import { X } from "lucide-react";
import { cn } from "@/lib/utils";

// =============================================================================
// Types
// =============================================================================

type ToastVariant = "info" | "success" | "warning" | "error";

export interface ToastOptions {
  id?: string;
  title?: string;
  description?: string;
  variant?: ToastVariant;
  durationMs?: number;
}

interface ToastInternal extends ToastOptions {
  id: string;
  createdAt: number;
}

interface ToastContextValue {
  showToast: (options: ToastOptions) => void;
  dismissToast: (id: string) => void;
}

// =============================================================================
// Context
// =============================================================================

const ToastContext = createContext<ToastContextValue | null>(null);

// =============================================================================
// Provider
// =============================================================================

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<ToastInternal[]>([]);

  const dismissToast = useCallback((id: string) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const showToast = useCallback(
    (options: ToastOptions) => {
      const id = options.id ?? crypto.randomUUID();
      const toast: ToastInternal = {
        id,
        createdAt: Date.now(),
        variant: options.variant ?? "info",
        ...options,
      };
      setToasts((prev) => [...prev, toast]);

      const duration = options.durationMs ?? 5000;
      if (duration > 0) {
        setTimeout(() => dismissToast(id), duration);
      }
    },
    [dismissToast]
  );

  return (
    <ToastContext.Provider value={{ showToast, dismissToast }}>
      {children}
      <div className="pointer-events-none fixed inset-x-0 bottom-0 z-50 flex flex-col items-center gap-2 p-4">
        {toasts.map((toast) => (
          <ToastItem key={toast.id} toast={toast} onDismiss={dismissToast} />
        ))}
      </div>
    </ToastContext.Provider>
  );
}

// =============================================================================
// Hook
// =============================================================================

export function useToast(): ToastContextValue {
  const ctx = useContext(ToastContext);
  if (!ctx) {
    throw new Error("useToast must be used within a ToastProvider");
  }
  return ctx;
}

// =============================================================================
// Toast Item Component
// =============================================================================

interface ToastItemProps {
  toast: ToastInternal;
  onDismiss: (id: string) => void;
}

function ToastItem({ toast, onDismiss }: ToastItemProps) {
  const { id, title, description, variant } = toast;

  const variantClasses = {
    success: "border-emerald-200 bg-emerald-50 text-emerald-900",
    error: "border-red-200 bg-red-50 text-red-900",
    warning: "border-amber-200 bg-amber-50 text-amber-900",
    info: "border-slate-200 bg-white text-slate-900",
  };

  return (
    <div
      className={cn(
        "pointer-events-auto w-full max-w-md rounded-lg border px-4 py-3 shadow-lg",
        "flex items-start gap-3 text-sm animate-in slide-in-from-bottom-2 fade-in duration-200",
        variantClasses[variant ?? "info"]
      )}
    >
      <div className="flex-1 min-w-0">
        {title && <div className="font-medium">{title}</div>}
        {description && (
          <div
            className={cn(
              "mt-0.5 text-xs",
              variant === "error" && "text-red-700",
              variant === "success" && "text-emerald-700",
              variant === "warning" && "text-amber-700",
              variant === "info" && "text-slate-600"
            )}
          >
            {description}
          </div>
        )}
      </div>
      <button
        type="button"
        onClick={() => onDismiss(id)}
        className={cn(
          "inline-flex h-5 w-5 flex-shrink-0 items-center justify-center rounded",
          "hover:bg-black/5 transition-colors"
        )}
      >
        <X className="h-3.5 w-3.5" />
      </button>
    </div>
  );
}
